import React, { useState, useEffect } from 'react';
import { Plus, Users, Settings as SettingsIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Staff as StaffType, StaffFilter, Tool } from '../types';
import { StaffModal } from '../components/StaffModal';
import { StaffTable } from '../components/StaffTable';
import { StaffFilterPanel } from '../components/StaffFilterPanel';
import { StaffSettings } from '../components/StaffSettings';

export function Staff() {
  const [staff, setStaff] = useState<StaffType[]>([]);
  const [filteredStaff, setFilteredStaff] = useState<StaffType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewStaff, setShowNewStaff] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [tools, setTools] = useState<Tool[]>([]);

  useEffect(() => {
    loadStaffAndTools();
  }, []);

  const loadStaffAndTools = async () => {
    try {
      const [staffResponse, toolsResponse] = await Promise.all([
        supabase
          .from('staff')
          .select(`
            id,
            name,
            role,
            bio,
            experience_level,
            employment_status,
            year_joined,
            created_at,
            tools:staff_tools (
              tool:tools (
                id,
                name
              )
            )
          `),
        supabase
          .from('tools')
          .select('*')
          .order('name')
      ]);

      if (staffResponse.error) throw staffResponse.error;
      if (toolsResponse.error) throw toolsResponse.error;

      const loadedStaff: StaffType[] = staffResponse.data.map(member => ({
        id: member.id,
        name: member.name,
        role: member.role,
        bio: member.bio,
        experience_level: member.experience_level,
        employment_status: member.employment_status,
        year_joined: member.year_joined,
        tools: member.tools?.map((t: any) => t.tool) || [],
        created_at: member.created_at
      }));

      setStaff(loadedStaff);
      setFilteredStaff(loadedStaff);
      setTools(toolsResponse.data);
    } catch (error) {
      console.error('Error loading staff and tools:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filter: StaffFilter) => {
    const filtered = staff.filter(member => {
      const matchesName = !filter.name || 
        member.name.toLowerCase().includes(filter.name.toLowerCase());

      const matchesRole = !filter.role || 
        member.role.toLowerCase().includes(filter.role.toLowerCase());

      const matchesExperience = !filter.experienceLevel || 
        member.experience_level === filter.experienceLevel;

      const matchesTools = filter.selectedTools.length === 0 || 
        filter.selectedTools.every(toolName => 
          member.tools.some(tool => 
            tool.name.toLowerCase() === toolName.toLowerCase()
          )
        );

      const matchesYear = !filter.yearJoined ||
        member.year_joined === filter.yearJoined;

      return matchesName && matchesRole && matchesExperience && matchesTools && matchesYear;
    });

    setFilteredStaff(filtered);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading team members...</div>
      </div>
    );
  }

  if (showSettings) {
    return (
      <StaffSettings
        onBack={() => setShowSettings(false)}
        onUpdate={loadStaffAndTools}
      />
    );
  }

  return (
    <div className="w-full px-4 py-6">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Users className="w-8 h-8 text-indigo-600 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Team Management</h1>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowNewStaff(true)}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Team Member
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <SettingsIcon className="w-4 h-4 mr-2" />
            Settings
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-3">
          <StaffFilterPanel
            data={staff}
            tools={tools}
            onFilterChange={handleFilterChange}
          />
        </div>
        <div className="col-span-9">
          <div className="bg-white shadow rounded-lg">
            <StaffTable
              staff={filteredStaff}
              tools={tools}
              onStaffUpdate={loadStaffAndTools}
            />
          </div>
        </div>
      </div>

      {showNewStaff && (
        <StaffModal
          staff={{
            id: '',
            name: '',
            role: '',
            bio: '',
            experience_level: 'junior',
            employment_status: 'w2',
            year_joined: '2018',
            tools: [],
            created_at: new Date().toISOString()
          }}
          tools={tools}
          onClose={() => setShowNewStaff(false)}
          onUpdate={() => {
            loadStaffAndTools();
            setShowNewStaff(false);
          }}
          isNew={true}
        />
      )}
    </div>
  );
}